import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState([]);
  const [consolidated, setConsolidatedState] = useState(false);
  const [period, setPeriod] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/companies").then((res) => {
      const list = res.data;
      setCompanies(list);
      const ids = list.map((c) => c.id);
      const primary = user.primary_company_id;
      setSelectedCompanyIds((prev) => {
        if (prev.length) return prev;
        // Default: a SINGLE plant (primary if assigned & visible, else first). Consolidation is OFF by default.
        if (primary && ids.includes(primary)) return [primary];
        return ids.length ? [ids[0]] : [];
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!selectedCompanyIds.length) {
      setPeriods([]);
      return;
    }
    const ids = selectedCompanyIds.join(",");
    api.get(`/periods?company_ids=${ids}`).then((res) => {
      setPeriods(res.data);
      setPeriod((prev) => (res.data.includes(prev) ? prev : res.data[0] || null));
    });
  }, [selectedCompanyIds]);

  // Single-plant selection (consolidation off): replace selection with one plant.
  const selectCompany = (id) => {
    setConsolidatedState(false);
    setSelectedCompanyIds([id]);
  };

  // Multi toggle, only meaningful while consolidated.
  const toggleCompany = (id) => {
    setSelectedCompanyIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const setConsolidated = (on) => {
    setConsolidatedState(on);
    if (on) {
      setSelectedCompanyIds(companies.map((c) => c.id));
    } else {
      const primary = user?.primary_company_id;
      setSelectedCompanyIds((prev) => {
        const pick = (primary && companies.some((c) => c.id === primary))
          ? primary : (prev[0] || companies[0]?.id);
        return pick ? [pick] : [];
      });
    }
  };

  const companyIdsParam = selectedCompanyIds.join(",");
  const isConsolidated = consolidated && selectedCompanyIds.length > 1;

  return (
    <AppContext.Provider
      value={{
        companies, periods,
        selectedCompanyIds, setSelectedCompanyIds, toggleCompany, selectCompany,
        consolidated, setConsolidated,
        companyIdsParam, isConsolidated,
        period, setPeriod,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
